import axios from 'axios';

// Determine the API base URL based on the current hostname
const getBaseUrl = () => {
    const hostname = window.location.hostname;
    
    // If we're on the production domain, use the AWS AppRunner backend
    if (hostname === 'app.skylighthouse.in') {
        return 'https://igqds3armu.us-east-1.awsapprunner.com/api';
    }
    
    // For local development, use localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:3001/api';
    }
    
    // For S3 website, use the AWS AppRunner backend
    if (hostname.includes('s3-website')) {
        return 'https://igqds3armu.us-east-1.awsapprunner.com/api';
    }
    
    // Default fallback
    return 'https://igqds3armu.us-east-1.awsapprunner.com/api';
};

const API_BASE_URL = process.env.REACT_APP_API_URL || getBaseUrl();

console.log('Using API URL:', API_BASE_URL);

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    withCredentials: true // Important: needed for cookies
});

// Log all requests
api.interceptors.request.use(
    (config) => {
        console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config);
        return config;
    },
    (error) => {
        console.error('[API] Request error:', error);
        return Promise.reject(error);
    }
);

// Log and handle responses
api.interceptors.response.use(
    (response) => {
        console.log(`[API Response] ${response.status} ${response.config.url}`, response.data);
        return response;
    },
    async (error) => {
        console.error('[API] Response error:', error);
        
        // Handle rate limiting with exponential backoff retry
        if (error.response?.status === 429 && !error.config.__isRetryRequest) {
            const retryCount = error.config.__retryCount || 0;
            
            // Only retry up to 3 times
            if (retryCount < 3) {
                console.debug(`[API] Rate limited, retrying (${retryCount + 1}/3)...`);
                
                // Exponential backoff: 1s, 2s, 4s
                const delay = Math.pow(2, retryCount) * 1000;
                
                // Wait for the delay
                await new Promise(resolve => setTimeout(resolve, delay));
                
                // Update retry count and flag
                const newConfig = {
                    ...error.config,
                    __isRetryRequest: true,
                    __retryCount: retryCount + 1
                };
                
                // Retry the request
                return api(newConfig);
            }
        }

        // Handle auth errors
        if (error.response?.status === 401) {
            console.debug('[API] Authentication error');
            window.dispatchEvent(new CustomEvent('auth-error'));
        }

        return Promise.reject(error);
    }
);

export interface Category {
    id: number;
    name: string;
    description: string;
    isActive: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface Product {
    id: number;
    name: string;
    description: string;
    price: number;
    stock: number;
    sku: string;
    barcode: string | null;
    imagePath: string;
    imageUrl: string;
    isActive: boolean;
    minStock: number;
    maxStock: number | null;
    cost: number;
    CategoryId: number;
    Category: Category;
    createdAt: string;
    updatedAt: string;
}

export interface ApiListResponse<T> {
    status: string;
    data: {
        categories?: T[];
        products?: T[];
    };
}

export interface ApiSingleResponse<T> {
    status: string;
    data: {
        category?: T;
        product?: T;
    };
}

export const categoriesApi = {
    getAll: () => api.get<{ status: string; data: { categories: Category[] } }>('/categories'),
    getById: (id: number) => api.get<{ status: string; data: { category: Category } }>(`/categories/${id}`)
};

export const productsApi = {
    getAll: () => api.get<{ status: string; data: { products: Product[] } }>('/products'),
    getByCategoryId: (categoryId: number) => api.get<{ status: string; data: { products: Product[] } }>(`/products?categoryId=${categoryId}`),
    getById: (id: number) => api.get<{ status: string; data: { product: Product } }>(`/products/${id}`)
};

export default api; 